import { invalidateReadIdentity, readIdentity, setReadIdentity, singleRead } from "./readFlight";
import { accountRequestSucceeded, recordAuthCheck, recordAuthRecovery } from "./authRecoveryState";
import { rememberRequest, clearRequestDiagnostics } from './requestDiagnostics';
// Cloud tokens are held by the local API, never by browser storage.
import { clearAiDrafts } from "./ai-drafts";
import { clearConnectionDiagnostics, rememberConnection } from './connectionDiagnostics';
export type AuthUser = { id: string; maskedPhone: string; mustChangePassword: boolean;
  feedbackSchemaVersion?: number; entitlement?: string; expiresAt?: string | null; products?: {sales:boolean;video:boolean;video_read_only?:boolean} };
export const hasSalesProduct = (user?: AuthUser | null) => user?.products?.sales !== false;
export type AuthStatus = { connected: boolean; user: AuthUser | null; reason: string; appId?: string; retryAfterSeconds?: number };
export type CodePurpose = "login" | "change_password";
export interface AuthService {
  connected: boolean;
  status(): Promise<AuthStatus>;
  recheck?(): Promise<AuthStatus>;
  session(): Promise<AuthUser | null>;
  sendCode(phone: string, purpose: CodePurpose): Promise<{ retryAfterSeconds: number }>;
  loginWithCode(phone: string, code: string): Promise<AuthUser>;
  loginWithPassword(phone: string, password: string): Promise<AuthUser>;
  changePassword(phone: string, code: string, password: string): Promise<void>;
  logout(): Promise<void>;
}
export const accountMessages: Record<string, string> = {
  SIGNED_OUT: "请先登录软件账号。",
  SESSION_REPLACED: "账号已在另一台电脑登录。本机正在安全交回最后的执行结果，不会启动新任务；完成后如需使用本机，请主动重新登录。",
  SESSION_REPLACED_DONE: "账号已在另一台电脑登录。本机已完成交回；如需使用本机，请主动重新登录并检查队列。",
  SESSION_REPLACED_TIMEOUT: "账号已在另一台电脑登录。收尾时间已到，请重新登录；未确认结果保留为结果待确认，请勿重复新建。",
  SESSION_REVOKED: "登录已失效，请重新登录。",
  SESSION_EXPIRED: "登录已过期，请重新登录。",
  ACCOUNT_DISABLED: "账号已停用，请联系工作人员。",
  ACCOUNT_EXPIRED: "账号订阅已到期，请联系工作人员续期。",
  APP_NOT_GRANTED: "账号尚未开通本软件，请联系工作人员。",
  APP_DISABLED: "软件授权已停用，请联系工作人员。",
  AUTH_UNAVAILABLE: "网络暂时不稳，正在自动重新连接……",
  SERVICE_BUSY: "服务器繁忙，请稍后重试",
  CONFIRMATION_OUTCOME_UNKNOWN: "确认结果尚未取得，请核对原请求，不要重复新建任务。",
  FEEDBACK_INVALID: "反馈内容包含不支持的信息，请移除敏感信息后重试。",
  FEEDBACK_RATE_LIMITED: "每小时最多反馈5次，请稍后重试。",
  FEEDBACK_TASK_NOT_FOUND: "任务不存在或不属于当前账号，请刷新任务后重试。",
  PASSWORD_CHANGE_REQUIRED: "请先修改初始密码，再使用业务功能。",
  AUTH_UNCONFIGURED: "账号服务尚未接入，暂不能登录，请联系工作人员。",
  INVALID_CREDENTIALS: "手机号或密码不正确。",
  LOGIN_THROTTLED: "密码尝试过多，请稍后再试。",
  INVALID_CODE: "验证码无效或已过期，请重新获取。",
  SMS_UNAVAILABLE: "短信服务暂不可用，请使用密码登录。",
  SMS_SEND_THROTTLED: "验证码发送过于频繁，请稍后重试。",
  VALIDATION_ERROR: "请检查手机号、验证码或密码格式。",
  DEVICE_REVOKED: "本机授权已撤销，请联系工作人员重新批准。",
  DEVICE_APPROVAL_REQUIRED: "本机需要工作人员批准后才能使用。",
  DEVICE_LIMIT_REACHED: "已达到允许的电脑数量，请联系工作人员。",
  SERVICE_SESSION_UNCERTAIN: "后台连接未能确认，请重新登录；系统不会自动重复签发。",
  SERVER_TIME_UNAVAILABLE: "无法确认云端执行时间，任务已暂停保留，请恢复连接后继续。",
  REFRESH_RECOVERY_EXPIRED: "登录凭据恢复已过期，请重新登录。",
  ACCOUNT_BINDING_NOT_CURRENT: "当前手机的平台执行位置已变化，请重新创建这批任务。",
  ACCOUNT_INVENTORY_INVALID: "本机手机平台执行位置不完整，请核对手机连接。",
  SALES_NOT_FOUND: "该业务资料不存在或当前账号无权查看，请刷新列表。",
  PROJECT_NOT_ACTIVE: "该项目已停用，请选择正在使用的项目。",
  PROJECT_VERSION_CONFLICT: "项目已被更新，请刷新后重新确认；本次未覆盖新内容。",
  RESOURCE_OWNERSHIP_CONFLICT: "该关键词或话术不属于当前项目，未保存修改。",
  RESOURCES_USE_DEDICATED_API: "请在关键词与话术列表中修改资源。",
  NODE_BINDING_MISMATCH: "执行电脑与任务绑定不一致，任务未启动。",
  NODE_CAPABILITY_MISMATCH: "当前电脑尚不支持此任务，请先完成更新。",
  NODE_SCOPE_CHANGED: "执行电脑的账号范围已变化，任务已停止领取。",
  TASK_EXPIRED: "任务已超过执行时限，不再启动。",
  TASK_GENERATION_MISMATCH: "任务版本已变化，请同步最新执行状态。",
  TASK_NOT_CLAIMED: "任务尚未被当前电脑领取，不能上报执行结果。",
  EVENT_SEQUENCE_CONFLICT: "执行记录顺序冲突，系统已保留现场等待核对。",
  EVENT_ID_CONFLICT: "执行记录内容冲突，系统不会覆盖原回执。",
  EVENT_GAP_TOO_LARGE: "尚有执行记录未同步，请恢复连接后重试。",
  EVENT_START_ORDER_CONFLICT: "任务开始记录顺序不正确，未采纳该回执。",
  EVENT_TERMINAL_ORDER_CONFLICT: "任务结束记录顺序不正确，未采纳该回执。",
  EXECUTION_ALREADY_STOPPED: "任务已停止，不会重新执行。",
  CANCEL_NOT_REQUESTED: "未找到对应停止请求，请刷新任务状态。",
  CREDITS_INSUFFICIENT: "积分不足，请联系工作人员处理。",
  CAPABILITY_UNAVAILABLE: "所需AI能力暂不可用，请联系管理员。",
  CAPABILITY_NOT_GRANTED: "账号尚未开通所需AI能力，请联系管理员。",
  QUOTA_EXHAUSTED: "AI生成服务暂不可用，请联系工作人员处理。",
  INPUT_TOO_LARGE: "提交内容超过AI服务限制，请缩小本次范围。",
  OUTPUT_SCHEMA_INVALID: "AI结果未通过校验，未采用该结果。",
  RESULT_EXPIRED: "该次AI结果已过期，系统不会自动重新收费执行。",
  IMAGE_UNAVAILABLE: "该次图片已过期或不可用，请重新读取当前页面。",
  IDEMPOTENCY_CONFLICT: "该次请求与已提交内容不一致，已停止重复提交。",
  CAPACITY_LIMIT: "AI服务繁忙，请稍后查询该次请求。",
  LOCAL_BUSINESS_ACCOUNT_MISMATCH: "本机业务资料属于另一个软件账号，请切回原账号或迁移本机资料。",
  LOCAL_BUSINESS_UNAVAILABLE: "本机业务服务暂不可用，请检查软件服务后重试。",
  LOCAL_BUSINESS_ROUTE_UNAVAILABLE: "本机业务服务与客户端版本不一致，请重启软件后重试。",
  LOCAL_BUSINESS_INVALID_RESPONSE: "本机业务服务返回无效数据，请重启软件后重试。",
  SALES_DEVICE_UNAVAILABLE: "当前电脑没有可用手机或任务能力，请刷新设备后重试。",
  SALES_GENERATION_FAILED: "本次AI生成已失败，原有关键词和话术未变；可重新点击发起新一次生成。",
  SALES_GENERATION_RECONCILE: "本次AI生成结果尚待核对，未发起新请求；请稍后重试查看原结果。",
  SALES_GENERATION_PENDING: "本次AI生成仍在进行，未发起新请求；请稍后重试查看原结果。",
};
export const isReplacementReason = (code: unknown) => typeof code === 'string' && ['SESSION_REPLACED', 'SESSION_REPLACED_DONE', 'SESSION_REPLACED_TIMEOUT'].includes(code);
export function accountMessage(code: string): string {
  const safe = /^[A-Z][A-Z0-9_]{2,79}$/.test(code) ? code : "AUTH_UNAVAILABLE";
  return accountMessages[safe] || `账号服务返回错误（${safe}），请重试或联系工作人员。`;
}
export class AuthFailure extends Error {
  constructor(public code: string) { super(accountMessage(code)); }
}
export class AuthUnavailable extends AuthFailure {
  constructor() { super("AUTH_UNCONFIGURED"); }
}
let connected = false;
let statusFlight: { generation: number; promise: Promise<AuthStatus>; controller: AbortController; forced: boolean } | undefined;
function request<T>(path: string, body?: unknown, signal?:AbortSignal): Promise<T> {
  if (body === undefined && !path.startsWith('/session')) return singleRead('client-auth:' + path,
    () => requestOnce<T>(path), signal);
  return requestOnce<T>(path, body, signal);
}
async function requestOnce<T>(path: string, body?: unknown, signal?:AbortSignal): Promise<T> {
  if (['/login/password','/login/sms','/logout','/password-reset'].includes(path)) {invalidateReadIdentity();recordAuthRecovery('');}
  if (path.startsWith('/login/')) {clearConnectionDiagnostics();clearRequestDiagnostics();}
  const diagnosticStart=performance.now();
  let diagnosticCode='ok', diagnosticError='none';
  let status: number | null = null;
  let received = false;
  const identity = readIdentity();
  try {
    const response = await fetch(`/api/client-auth${path}`, { method: body === undefined ? "GET" : "POST",
      credentials: "same-origin", cache: "no-store", signal, headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body) });
    status = response.status;
    received = true;
    const value = await response.json();
    if (!response.ok) {
      const code = typeof value?.code === 'string' ? value.code : 'AUTH_UNAVAILABLE';
      diagnosticCode=code;diagnosticError='http_status';
      if (code === 'AUTH_UNAVAILABLE') rememberConnection(path === '/session' ? 'auth_status' : 'auth_request', 'http_status', status);
      throw new AuthFailure(code);
    }
    if(path.startsWith('/session') && (typeof value?.connected!=='boolean' || typeof value?.reason!=='string' || !(value.user===null || typeof value.user?.id==='string' && !!value.user.id))){
      diagnosticCode='other';diagnosticError='envelope';
      rememberConnection('auth_status','envelope',status);
      throw new AuthFailure('AUTH_UNAVAILABLE');
    }
    if (!path.startsWith('/session')) accountRequestSucceeded(identity);
    return value as T;
  } catch (error) {
    if (error instanceof AuthFailure) throw error;
    diagnosticError=received?'json_parse':'transport';
    rememberConnection(path === '/session' ? 'auth_status' : 'auth_request', received ? 'json_parse' : 'fetch', status);
    throw new AuthFailure("AUTH_UNAVAILABLE");
  } finally { rememberRequest(path,body===undefined?'GET':'POST',status,diagnosticCode,diagnosticError,performance.now()-diagnosticStart); }
}
function loadStatus(force = false): Promise<AuthStatus> {
    const generation = readIdentity();
    if (statusFlight?.generation === generation && (!force || statusFlight.forced)) return statusFlight.promise;
    if (force) statusFlight?.controller.abort();
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 25_000);
    recordAuthCheck();
    const promise = request<AuthStatus>(force ? '/session?force=true' : '/session', undefined, controller.signal).then(value => {
      if (readIdentity() !== generation) throw new AuthFailure('SIGNED_OUT');
      if (controller.signal.aborted) throw new AuthFailure('AUTH_UNAVAILABLE');
      connected = value.connected;
      recordAuthRecovery(value.reason || '');
      if (value.user) setReadIdentity(JSON.stringify([value.appId, value.user.id, value.user.products]));
      return value;
    }, error => {
      if (statusFlight?.promise === promise) recordAuthRecovery(error instanceof AuthFailure ? error.code : 'AUTH_UNAVAILABLE');
      throw error;
    });
    statusFlight = {generation, promise, controller, forced: force};
    const retire = () => { window.clearTimeout(timeout); if (statusFlight?.promise === promise) statusFlight = undefined; };
    void promise.then(retire, retire);
    return promise;
}
export const authService: AuthService = {
  get connected() { return connected; },
  status: () => loadStatus(),
  recheck: () => loadStatus(true),
  session: async () => (await authService.status()).user,
  sendCode: (phone, purpose) => request("/code", { phone, purpose }),
  loginWithCode: (phone, code) => request("/login/sms", { phone, code }),
  loginWithPassword: (phone, password) => request("/login/password", { phone, password }),
  changePassword: async (phone, code, password) => { await request("/password-reset", { phone, code, password }); },
  logout: async () => { await request("/logout", {}); clearAiDrafts(); clearConnectionDiagnostics(); clearRequestDiagnostics(); },
};
export const videoWorkshopService = {
  status: (signal?:AbortSignal) => request<{ enabled: boolean; entryUrl: string | null; ready: boolean }>("/video-workshop", undefined, signal),
  ticket: (browser_challenge: string) => request<{ ticket: string; account_id: string; app_id: string; expires_at: string }>(
    "/video-workshop/ticket", { browser_challenge }),
};
export const validPhone = (value: string) => /^1[3-9]\d{9}$/.test(value);
export const validCode = (value: string) => /^\d{6}$/.test(value);
